// Time helpers. The app assumes the browser runs in the tenant timezone (true
// for on-site tablets), so wall-clock rendering uses local Date getters.

export function hmsToMinutes(hms: string): number {
  const [h = '0', m = '0'] = hms.split(':');
  return Number(h) * 60 + Number(m);
}

export function minutesToLabel(min: number, ampm = true): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!ampm) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** Local minutes-of-day for an ISO instant (browser/tenant tz). */
export function localMinutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/** Build an ISO instant for `dateStr` (YYYY-MM-DD) at `minutes` local time. */
export function isoForDateMinutes(dateStr: string, minutes: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setMinutes(minutes);
  return d.toISOString();
}

export function dayBoundsIso(dateStr: string): { from: string; to: string } {
  const start = new Date(`${dateStr}T00:00:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function fmtTimeRange(startIso: string, endIso: string): string {
  return `${minutesToLabel(localMinutesOfDay(startIso))} – ${minutesToLabel(localMinutesOfDay(endIso))}`;
}

/** Short local stamp used by the audit log and the booking activity trail. */
export function shortDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const ISO_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?/g;

/**
 * Audit values are stored as raw strings, and several actions embed an ISO
 * instant (e.g. `Noura @ 2026-07-25T08:30:00.000Z`). Rewrite every such stamp
 * to `shortDateTime`, leaving the surrounding text untouched.
 */
export function prettyAuditValue(v: string): string {
  return v.replace(ISO_RE, (m) => (Number.isNaN(new Date(m).getTime()) ? m : shortDateTime(m)));
}

export function prettyDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
